import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Upload, Trophy, Award, CheckCircle2, FileText } from "lucide-react";

export function LmsAssignments({ cid }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/lms/courses/${cid}/assignments`).then(({ data }) => setItems(data.assignments)).catch(() => {});
  useEffect(() => { load(); }, [cid]);
  const submit = async (aid, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/lms/assignments/${aid}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Devoir soumis"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!items.length) return <p className="text-sm text-slate-400">Aucun devoir pour ce cours.</p>;
  return (
    <div className="space-y-3" data-testid="lms-assignments">
      {items.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 p-4" data-testid={`assignment-${a.id}`}>
          <p className="font-medium text-navy">{a.title}</p>
          {a.instructions && <p className="mt-1 text-sm text-slate-500">{a.instructions}</p>}
          {a.my_submission ? (
            <div className="mt-2 text-sm">
              <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 size={14} /> Soumis</span>
              {a.my_submission.status === "graded" && <span className="ml-2 font-semibold text-[#0E7C7B]">Note : {a.my_submission.grade}/{a.max_grade}</span>}
              {a.my_submission.comment && <p className="mt-1 text-xs text-slate-500">Formateur : {a.my_submission.comment}</p>}
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs text-[#0E7C7B]"><Upload size={13} /> Remplacer<input type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => submit(a.id, e.target.files?.[0])} /></label>
            </div>
          ) : (
            <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-2 text-sm text-slate-500 hover:border-[#0E7C7B]" data-testid={`assignment-upload-${a.id}`}><Upload size={14} /> Soumettre (PDF, Word, image)<input type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => submit(a.id, e.target.files?.[0])} /></label>
          )}
        </div>
      ))}
    </div>
  );
}

export function LmsQuizzes({ cid }) {
  const [quizzes, setQuizzes] = useState([]);
  const [active, setActive] = useState(null); // {quiz, questions}
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState(null);
  const load = () => api.get(`/lms/courses/${cid}/quizzes`).then(({ data }) => setQuizzes(data.quizzes)).catch(() => {});
  useEffect(() => { load(); }, [cid]);
  const open = async (q) => { setResult(null); setAnswers({}); setBoard(null); try { const { data } = await api.get(`/lms/quizzes/${q.id}`); setActive(data); } catch (e) { toast.error(apiError(e)); } };
  const submit = async () => {
    try { const { data } = await api.post(`/lms/quizzes/${active.id}/submit`, { answers }); setResult(data); load(); const lb = await api.get(`/lms/quizzes/${active.id}/leaderboard`); setBoard(lb.data.leaderboard); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (active) {
    return (
      <div className="rounded-xl border border-slate-200 p-4" data-testid="lms-quiz-take">
        <h3 className="font-display font-semibold text-navy">{active.title}</h3>
        {result ? (
          <div className="mt-3" data-testid="lms-quiz-result">
            <p className={`text-2xl font-bold ${result.passed ? "text-emerald-600" : "text-amber-600"}`}>{result.score_pct}%</p>
            <p className="text-sm text-slate-500">{result.passed ? "Réussi" : `Non réussi (seuil ${result.pass_score}%)`} · {result.earned}/{result.total} points</p>
            {result.certificate_url && <a href={`${API}${result.certificate_url.slice(4)}`} target="_blank" rel="noreferrer" data-testid="lms-certificate-link" className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#0E7C7B] px-4 py-2 text-sm font-semibold text-white"><Award size={15} /> Télécharger mon certificat</a>}
            {board && <div className="mt-4"><p className="mb-1 flex items-center gap-1 text-sm font-semibold text-navy"><Trophy size={15} className="text-amber-500" /> Classement</p>{board.map((b) => <div key={b.rank} className={`flex justify-between rounded px-2 py-1 text-sm ${b.me ? "bg-[#0E7C7B]/10 font-semibold" : ""}`}><span>{b.rank}. {b.name}{b.me ? " (vous)" : ""}</span><span>{b.score_pct}%</span></div>)}</div>}
            <Button variant="outline" onClick={() => setActive(null)} className="mt-3 rounded-full text-sm">Fermer</Button>
          </div>
        ) : (
          <div className="mt-3 space-y-4">
            {active.questions.map((q, i) => (
              <div key={q.id} data-testid={`quiz-q-${i}`}>
                <p className="text-sm font-medium text-navy">{i + 1}. {q.text}</p>
                {q.type === "mcq" && q.options.map((o, oi) => <label key={oi} className="mt-1 flex items-center gap-2 text-sm"><input type="radio" name={q.id} onChange={() => setAnswers({ ...answers, [q.id]: String(oi) })} data-testid={`quiz-${i}-opt-${oi}`} /> {o}</label>)}
                {q.type === "truefalse" && ["Vrai", "Faux"].map((o, oi) => <label key={oi} className="mt-1 flex items-center gap-2 text-sm"><input type="radio" name={q.id} onChange={() => setAnswers({ ...answers, [q.id]: oi === 0 })} data-testid={`quiz-${i}-tf-${oi}`} /> {o}</label>)}
                {q.type === "short" && <Input className="mt-1" placeholder="Votre réponse" onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`quiz-${i}-short`} />}
              </div>
            ))}
            <div className="flex gap-2"><Button onClick={submit} data-testid="lms-quiz-submit" className="rounded-full bg-[#0E7C7B] text-white">Valider mes réponses</Button><Button variant="outline" onClick={() => setActive(null)} className="rounded-full">Annuler</Button></div>
          </div>
        )}
      </div>
    );
  }
  if (!quizzes.length) return <p className="text-sm text-slate-400">Aucune composition pour ce cours.</p>;
  return (
    <div className="space-y-3" data-testid="lms-quizzes">
      {quizzes.map((q) => (
        <div key={q.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-4" data-testid={`quiz-${q.id}`}>
          <div>
            <p className="font-medium text-navy">{q.title}</p>
            <p className="text-[11px] text-slate-400">{q.questions_count} question(s) · seuil {q.pass_score}%{q.best_score != null ? ` · meilleur ${q.best_score}%` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {q.passed && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 size={14} /> Réussi</span>}
            <Button size="sm" onClick={() => open(q)} data-testid={`quiz-start-${q.id}`} className="rounded-full bg-navy text-xs text-white"><FileText size={13} className="mr-1" /> {q.best_score != null ? "Refaire" : "Passer"}</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
